import {blue} from "@ant-design/colors";
import {Button, Checkbox, Collapse, Divider, message, Modal, Radio, Space} from "antd";
import RadioGroup from "antd/lib/radio/group";
import React, {FC, memo, useEffect, useRef, useState} from "react";
import {useHistory} from "react-router-dom";
import {CheckboxChangeEvent} from "antd/lib/checkbox";
import {
    EditCourseInput,
    GetBookingsByCourseIdWithCourseLessonBookingsOptimizedDocument,
    GetCourseByIdDocument,
    GetCourseDetailsByIdDocument,
    GetCourseLessonsByCourseIdDocument,
    GetCoursesWithPaginationDocument,
    GetDetailedCourseTypesDocument,
    GetInstructorsDocument,
    useUpdateCourseMutation,
} from "../../generated/graphql";
import {URL_COURSES_EDIT} from "../../models/url";
import i18n from "../../services/i18n";
import {colorDarkGrey} from "../../styles/colors";
import css from "./IncreaseMaxAttendeesModal.less";

export type OptionType = {
    key: "option1" | "option2";
    buttonText: string;
    icon: string;
    action: Function;
    loading: boolean;
    title: string;
    description: string | React.ReactNode;
    successMessage?: string | React.ReactNode;
};

type IncreaseMaxAttendeesModalProps = {
    open: boolean;
    setModalVisible: (e: boolean) => void;
    courseId: string;
    editCourseData: EditCourseInput;
    fromPathUrl?: string;
    // onConfirm: (option: OptionType["key"]) => void;
    // onCancel: () => void;
};

const IncreaseMaxAttendeesModal: FC<IncreaseMaxAttendeesModalProps> = ({
    open,
    setModalVisible,
    courseId,
    editCourseData,
    fromPathUrl,
    // onConfirm,
    // onCancel,
}) => {
    const history = useHistory();
    const [notifyBooker, setNotifyBooker] = useState(false);

    const isMounted = useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    // UPDATE COURSE QUERY --------------------------------------------------------------------------------------------------

    const [updateCourse] = useUpdateCourseMutation({
        refetchQueries: [
            "GetCoursesWithPagination",
            {
                query: GetCoursesWithPaginationDocument,
            },
            "GetInstructors",
            {
                query: GetInstructorsDocument,
            },
            "GetDetailedCourseTypes",
            {
                query: GetDetailedCourseTypesDocument,
            },
            "GetCourseLessonsByCourseId",
            {
                query: GetCourseLessonsByCourseIdDocument,
                variables: {
                    //   courseId: course.id,
                    courseId,
                },
            },
            "GetCourseById",
            {
                query: GetCourseByIdDocument,
                variables: {
                    id: courseId,
                },
            },
            "GetBookingsByCourseIdWithCourseLessonBookingsOptimized",
            {
                query: GetBookingsByCourseIdWithCourseLessonBookingsOptimizedDocument,
                variables: {
                    courseId,
                },
            },
            "GetCourseDetailsById",
            {
                query: GetCourseDetailsByIdDocument,
                variables: {
                    id: courseId,
                },
            },
        ],
    });

    // OPTIONS --------------------------------------------------------------------------------------------

    const options: Array<OptionType> = [
        {
            key: "option1",
            buttonText: "Freie Kursplätze automatisch mit Warteliste befüllen",
            icon: "setting",
            action: () => console.log("option1"),
            // loading: removeBookingLoading,
            loading: false,
            title: "Auto",
            description:
                "Die frei werdenden Kursplätze werden automatisch mit den ältesten Wartelistenplätzen befüllt.\nDu kannst entscheiden, ob der Bucher der neuen Buchung automatisch benachrichtigt werden soll.",
            successMessage: "Die Kursplätze wurden erfolgreich mit der Warteliste befüllt.",
        },
        {
            key: "option2",
            buttonText: "In eine Platzreservierung umwandeln",
            icon: "pushpin",
            action: () => console.log("option2"),
            // loading: removeBookingLoading,
            loading: false,
            title: "Platzreservierung",
            description: `Damit die freiwerdenden Kursplätze nicht aus dem Web gebucht werden können,
                    wird jeweils eine Platzreservierung an die Stelle des freien Platzes treten.
                    Diese kann zu einem beliebigen Zeitpunkt an einen neuen Teilnehmer vergeben werden.`,
            successMessage: "Die Kursplätze wurden erfolgreich in Platzreservierungen umgewandelt.",
        },
    ];

    // NOTIFY USER --------------------------------------------------------------------------------------------

    // const changeNotifyUser = (e: boolean) => {

    //     setNotifyBooker(e);
    // };

    const [selectedOption, setSelectedOption] = useState<OptionType["key"]>(options[0].key);

    const onChange = (option: OptionType) => {
        setSelectedOption(option.key);
    };

    const RadioSolution = () => {
        const {Panel} = Collapse;

        const getStyle = (key: OptionType["key"]) => {
            const both = {
                borderRadius: 6,
                marginBottom: 10,
                whiteSpace: "pre-wrap",
            };

            if (key === selectedOption) {
                return {
                    background: blue[5],
                    border: `2px solid ${blue[5]}`,
                    ...both,
                };
            }

            return {
                background: "transparent",
                border: `2px solid lightgrey`,
                ...both,
            };
        };

        const getTextStyle = (key: OptionType["key"]) => {
            const both = {
                display: "flex",
                alignItems: "center",
                fontWeight: 600,
            };

            if (key === selectedOption) {
                return {
                    color: "white",
                    fontSize: "1.1rem",
                    ...both,
                };
            }

            return {
                background: "transparent",
                fontSize: "0.9rem",
                color: colorDarkGrey,
                ...both,
            };
        };

        return (
            <RadioGroup defaultValue={selectedOption}>
                <Collapse
                    style={{borderColor: "transparent", marginTop: 20}}
                    activeKey={selectedOption}
                    onChange={(key: string | Array<string>) => setSelectedOption(Array.isArray(key) ? (key[0] as OptionType["key"]) : (key as OptionType["key"]))}
                >
                    {options.map((option) => {
                        return (
                            <Panel
                                style={getStyle(option.key)}
                                key={option.key}
                                showArrow={false}
                                // extra="extra"
                                header={
                                    <Radio className={css.radioButton} style={getTextStyle(option.key)} value={option.key} checked={option.key === selectedOption} onChange={() => onChange(option)}>
                                        {option.buttonText}
                                    </Radio>
                                }
                            >
                                <div>{option.description}</div>
                            </Panel>
                        );
                    })}
                </Collapse>
            </RadioGroup>
        );
    };

    // MODAL BUTTON TEXT ----------------------------------------------------------------------------------

    const buttonText = (key: OptionType["key"]) => {
        switch (key) {
            case "option1":
                return i18n.containers.bookings.BookingList.waitingListActions.deleteOption1();
            case "option2":
                return i18n.containers.bookings.BookingList.waitingListActions.deleteOption2();
            default:
                return i18n.containers.bookings.BookingList.waitingListActions.deleteOption1();
        }
    };

    // HANDLE OK -----------------------------------------------------------------------------------------

    const handleCancel = () => {
        // onCancel();
        setModalVisible(false);
    };

    const [loading, setLoading] = useState(false);

    const handleOk = async () => {
        const selected = options.find((option) => option.key === selectedOption);
        const key = selected?.key;

        setLoading(true);
        let mutationSuccess = false;
        let mutationError: Error | null = null;

        switch (key) {
            case "option1":
                try {
                    message.info("Kurs würde aktualisiert mit option 1...");
                    await updateCourse({
                        variables: {
                            id: courseId,
                            fillFromWaitingList: true,
                            editCourseData,
                            notifyBooker,
                        },
                    });
                    mutationSuccess = true;
                } catch (error) {
                    console.log("updateCourse option1:::", error);
                    mutationSuccess = false;
                    // FIX: Verwende Fehler aus catch Block statt stale mutationError aus Hook
                    mutationError = error instanceof Error ? error : new Error(String(error));
                } finally {
                    if (isMounted.current) {
                        // FIX: setLoading(false) wird immer aufgerufen, unabhängig von Erfolg/Fehler
                        setLoading(false);

                        if (mutationError) {
                            message.error(mutationError.message);
                        } else if (mutationSuccess) {
                            setModalVisible(false);
                            message.success(selected?.successMessage ?? "Kurs wurde aktualisiert");
                            // Navigiere direkt zu fromPathUrl (Kursdetails) falls vorhanden
                            if (fromPathUrl) {
                                history.push(fromPathUrl);
                            }
                        }
                    }
                }
                break;
            case "option2":
                try {
                    message.info("Kurs würde aktualisiert mit option 2...");
                    await updateCourse({
                        variables: {
                            id: courseId,
                            fillFromWaitingList: false,
                            editCourseData,
                            notifyBooker: false,
                        },
                    });
                    mutationSuccess = true;
                } catch (error) {
                    console.log("updateCourse option2:::", error);
                    mutationSuccess = false;
                    // FIX: Verwende Fehler aus catch Block statt stale mutationError aus Hook
                    mutationError = error instanceof Error ? error : new Error(String(error));
                } finally {
                    if (isMounted.current) {
                        // FIX: setLoading(false) wird immer aufgerufen, unabhängig von Erfolg/Fehler
                        setLoading(false);

                        if (mutationError) {
                            message.error(mutationError.message);
                        } else if (mutationSuccess) {
                            setModalVisible(false);
                            message.success(selected?.successMessage ?? "Kurs wurde aktualisiert");
                            // Navigiere direkt zu fromPathUrl (Kursdetails) falls vorhanden
                            if (fromPathUrl) {
                                history.push(fromPathUrl);
                            }
                        }
                    }
                }
                break;
            default:
                console.log("notCovered: ", key);
                break;
        }

        // FIX: Stelle sicher, dass setLoading(false) immer aufgerufen wird,
        // auch wenn kein Switch-Case matched (z.B. wenn selectedOption undefined ist)
        if (!mutationSuccess && !mutationError) {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    // RENDER --------------------------------------------------------------------------------------------

    return (
        <Modal
            title="Umgang mit freien Kursplätzen"
            open={open}
            onCancel={handleCancel}
            footer={
                <div style={{display: "flex", justifyContent: "center"}}>
                    <Button type="danger" ghost block onClick={handleCancel}>
                        {"Abbrechen"}
                    </Button>
                    <Button
                        type="primary"
                        block
                        loading={loading}
                        onClick={async () => {
                            await handleOk();
                        }}
                    >
                        {buttonText(selectedOption)}
                    </Button>
                </div>
            }
        >
            <div>
                <p>{"Die maximale Teilnehmerzahl des Kurses wurde erhöht und es gibt bereits Teilnehmer auf der Warteliste."}</p>
                <p>{"Du hast hier die Möglichkeit, zu entscheiden, was mit den zusätzlichen Plätzen passieren soll."}</p>
                <RadioSolution />
                {selectedOption === "option1" && (
                    <>
                        <Divider />
                        <Space
                            size={15}
                            style={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            <span>{"Buchungsbestätigung automatisch versenden"}</span>
                            <Checkbox onChange={() => setNotifyBooker(true)} checked={notifyBooker}>
                                {"Ja"}
                            </Checkbox>
                            <Checkbox onChange={() => setNotifyBooker(false)} checked={!notifyBooker}>
                                {"Nein"}
                            </Checkbox>
                        </Space>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default memo(IncreaseMaxAttendeesModal);
